/**
 * 极简 Code128B 条形码 → SVG data URL。自带编码，不引第三方库。
 * 覆盖 ASCII 32–126（数字/大小写字母/常见符号），足够单据编号/SKU 场景。
 */

// Code128 每个字符的条空宽度模式（11 模块），索引 = 码值。
const PATTERNS = [
  '11011001100','11001101100','11001100110','10010011000','10010001100','10001001100','10011001000','10011000100','10001100100','11001001000',
  '11001000100','11000100100','10110011100','10011011100','10011001110','10111001100','10011101100','10011100110','11001110010','11001011100',
  '11001001110','11011100100','11001110100','11101101110','11101001100','11100101100','11100100110','11101100100','11100110100','11100110010',
  '11011011000','11011000110','11000110110','10100011000','10001011000','10001000110','10110001000','10001101000','10001100010','11010001000',
  '11000101000','11000100010','10110111000','10110001110','10001101110','10111011000','10111000110','10001110110','11101110110','11010001110',
  '11000101110','11011101000','11011100010','11011101110','11101011000','11101000110','11100010110','11101101000','11101100010','11100011010',
  '11101111010','11001000010','11110001010','10100110000','10100001100','10010110000','10010000110','10000101100','10000100110','10110010000',
  '10110000100','10011010000','10011000010','10000110100','10000110010','11000010010','11001010000','11110111010','11000010100','10001111010',
  '10100111100','10010111100','10010011110','10111100100','10011110100','10011110010','11110100100','11110010100','11110010010','11011011110',
  '11011110110','11110110110','10101111000','10100011110','10001011110','10111101000','10111100010','11110101000','11110100010','10111011110',
  '10111101110','11101011110','11110101110','11010000100','11010010000','11010011100','1100011101011',
];
const START_B = 104;
const STOP = 106;

/**
 * 内容无法用 Code128B 编码时返回它，而不是一张缺字符的条码。渲染器认得这个
 * 值并原样印成提示 —— 判据是「不以 data: 开头」，所以文案可以随便改。
 */
export const CODE128_UNSUPPORTED = '条码内容含中文或特殊字符，Code128 无法编码，请改用二维码';

/** value 编码为 Code128B SVG data URL。moduleW = 单模块像素宽，height = 条高。 */
export function code128ToSvgDataUrl(value: string, opts?: { moduleW?: number; height?: number; showText?: boolean }): string {
  const mod = opts?.moduleW ?? 2;
  const h = opts?.height ?? 60;
  const showText = opts?.showText !== false;
  // Code128B 只覆盖 ASCII 32-126。以前把中文等字符静默丢掉，印出来的条码
  // 扫出来和记录对不上（「沪A0001」变成「A0001」），比不印更危险 —— 直接放弃，
  // 由调用方决定提示或改用二维码。
  const chars = [...String(value)];
  if (chars.length === 0) return '';
  if (chars.some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) > 126)) return CODE128_UNSUPPORTED;
  const codes = [START_B];
  let sum = START_B;
  chars.forEach((c, i) => {
    const v = c.charCodeAt(0) - 32;
    codes.push(v);
    sum += v * (i + 1);
  });
  codes.push(sum % 103); // 校验码
  codes.push(STOP);

  const bits = codes.map((c) => PATTERNS[c]).join('');
  const barsW = bits.length * mod;
  const textH = showText ? 16 : 0;
  const totalH = h + textH;

  let x = 0;
  const rects: string[] = [];
  for (const b of bits) {
    if (b === '1') rects.push(`<rect x="${x}" y="0" width="${mod}" height="${h}" fill="#000"/>`);
    x += mod;
  }
  const text = showText
    ? `<text x="${barsW / 2}" y="${h + 13}" font-family="monospace" font-size="13" text-anchor="middle" fill="#000">${escapeXml(chars.join(''))}</text>`
    : '';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${barsW}" height="${totalH}" viewBox="0 0 ${barsW} ${totalH}"><rect width="${barsW}" height="${totalH}" fill="#fff"/>${rects.join('')}${text}</svg>`;
  return `data:image/svg+xml;base64,${utf8ToBase64(svg)}`;
}

/**
 * UTF-8 安全的 base64，浏览器 / Worker / Node 三处都能跑。
 * `btoa` 只吃 latin1，直接喂含中文的 SVG 会抛 InvalidCharacterError，
 * 所以先编码成字节再逐字节转 latin1 字符串。
 */
function utf8ToBase64(s: string): string {
  const buf = (globalThis as { Buffer?: { from(s: string, enc: string): { toString(enc: string): string } } })
    .Buffer;
  if (buf) return buf.from(s, 'utf8').toString('base64');
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  // 分块拼接：一次性展开成参数会在长内容上把调用栈撑爆。
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
